import { useCallback } from 'react';
import type { DoiTuongSearchParams } from '@/types/schemas';
import { getItemColumns } from '../cols-definition';
import { useDynamicSearch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const EMPTY_PARAMS = {};

interface UseSearchTableProps {
  searchData: (filters: DoiTuongSearchParams, page?: number, searchPageSize?: number) => Promise<void>;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({ searchData, isFiltered, handlePageChange }: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: DoiTuongSearchParams) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: DoiTuongSearchParams, onSearchFn: (params: DoiTuongSearchParams) => Promise<void>) => {
      await onSearchFn(formData);
    },
    []
  );

  const getColumns = useCallback(() => {
    const cols = getItemColumns();
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, []);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: EMPTY_PARAMS,
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.VAT_TU}/filter`,
    pageSize: 100
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData({}, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
